import { useMemo } from 'react'

import { DateTime } from 'luxon'

import { digObject } from '@domain-group/javascript-utils'

import { useWatchEntityUpdates } from '@domain-group/react-hooks'

import type { OrderItemModel } from '@models/types'

import useAgencySelector from '@hooks/useAgencySelector'
import useOrderItems from '@hooks/useOrderItems'
import useOrders from '@hooks/useOrders'
import useCurrentUser from '@hooks/useCurrentUser'
import useSelector from '@hooks/useSelector'

import defaultRequestOptions from '@sections/Client/packs/Overviews/defaultRequestOptions'

import { EntitiesState } from '@redux/entities'
import sortByPriorityCampaigns from '@functions/priorityCampaign'

const watchEntityKeys = ['orderItems', 'statuses']

type GenerateUrlsOptions = {
  prefixRoute?: string,
}

export const generateUrls = (options?: GenerateUrlsOptions) => {
  const { prefixRoute = 'systemManager' } = options || {}

  return {
    allOrdersIndexUrl: `#/${prefixRoute}/orders?status=all`,
    artworkApprovedOrdersIndexUrl: `#/${prefixRoute}/orders?status=artwork-approved`,
    artworkPendingOrdersIndexUrl: `#/${prefixRoute}/orders?status=artwork-pending`,
    completedOrdersIndexUrl: `#/${prefixRoute}/orders?status=complete`,
    pendingOrdersIndexUrl: `#/${prefixRoute}/orders?status=pending`,
  }
}

type GroupedOrderItems = {
  [key: string]: {
    key: string,
    orderItems: OrderItemModel[],
    title: string,
  },
};

type SortByPriorityOrderItemsParams = {
  groupedItems: GroupedOrderItems,
  orders: EntitiesState['orders'],
  priorityOnly: boolean,
  selectedPriorityUserId: number,
}

const sortByPriorityOrderItems = (params: SortByPriorityOrderItemsParams) => {
  const {
    groupedItems, orders, priorityOnly, selectedPriorityUserId,
  } = params

  const sortedFilteredItems = Object.values(groupedItems).map((group) => {
    const filteredOrderItems = group.orderItems
      .filter((orderItem) => {
        const order = orders[orderItem.order_id]
        // If priorityOnly is true, filter out order items
        if (priorityOnly && (!order
            || !order.priority_campaign_user_ids?.includes(selectedPriorityUserId))){
          return false
        }
        // Assign priority_campaign_user_ids from order to order items as we are hitting orders controller
        if (order){
          orderItem.priority_campaign_user_ids = order.priority_campaign_user_ids
        }

        return true
      })

    // sortKey defaults to 'cache_key desc' which overrides previous sorts by dueDate etc
    sortByPriorityCampaigns({ entities: filteredOrderItems, selectedPriorityUserId, sortKey: '' })

    group.orderItems = filteredOrderItems

    return group
  })

  return sortedFilteredItems
}

const getGroupKey = (dueDate: string) => {
  const formattedDueDate = DateTime.fromFormat(dueDate, 'dd/MM/yyyy')
  const dateNow = DateTime.now()

  // overdue
  if (formattedDueDate && dateNow >= formattedDueDate) return 'overdue'

  // due later
  if (formattedDueDate && dateNow < formattedDueDate) return 'dueLater'

  return 'noDueDate'
}

// ensure items with the same due date are ordered by updated date
const sortByDueDateAndUpdatedDate = (orderItems: OrderItemModel[]) => orderItems.sort((a, b) => {
  const dueDateA = digObject(a, 'dates.due.date', '')
  const dueDateB = digObject(b, 'dates.due.date', '')

  const dueDateTimeA = DateTime.fromFormat(dueDateA, 'dd/MM/yyyy').isValid
    ? DateTime.fromFormat(dueDateA, 'dd/MM/yyyy').toMillis() : Infinity
  const dueDateTimeB = DateTime.fromFormat(dueDateB, 'dd/MM/yyyy').isValid
    ? DateTime.fromFormat(dueDateB, 'dd/MM/yyyy').toMillis() : Infinity

  if (dueDateTimeA === dueDateTimeB){
    return b.cache_key - a.cache_key // Descending order for updated date
  }

  return dueDateTimeA - dueDateTimeB // Ascending order for due date
})

const groupOrderItemsByDueDate = (orderItems: OrderItemModel[]) => {
  const grouped = {
    overdue: {
      key: 'overdueOrders',
      orderItems: [],
      title: 'Overdue Orders',
    },
    dueLater: {
      key: 'dueLater',
      orderItems: [],
      title: 'Due Later',
    },
    noDueDate: {
      key: 'noDateSet',
      orderItems: [],
      title: 'No Date Set',
    },
  }
  const sortedOrderItems = sortByDueDateAndUpdatedDate(orderItems)

  sortedOrderItems.forEach((orderItem) => {
    const dueDate = digObject(orderItem, 'dates.due.date', '')
    const groupKey = getGroupKey(dueDate)

    // Add to Group
    // @ts-expect-error TS2345
    grouped[groupKey].orderItems.push(orderItem)
  })

  return grouped
}

type OrderOverviewsFilters = {
  status_group_key?: string,
  [key: string]: any,
}

type UseOrdersOverviewOptions = {
  filters?: OrderOverviewsFilters,
}

function useOrdersOverview(options: UseOrdersOverviewOptions) {
  const { filters = {} } = options
  const { priority_orders_by_user_id: priorityCampaignUserId } = filters || {}

  const { currentUser, isAgent, isEbuAdmin } = useCurrentUser()
  const selectedPriorityUserId = priorityCampaignUserId || currentUser.id

  const { selectedAgencyId } = useAgencySelector()

  const { loading } = useOrders({
    // @ts-expect-error TS2322
    filters: {
      agency_id: isAgent || isEbuAdmin ? undefined : selectedAgencyId,
      exclude_hidden_campaigns: true,
      order_by: 'updated_at desc',
      ...filters,
    },
    performHttpRequests: true,
    requestOptions: {
      ...defaultRequestOptions.order,
    },
    sortBy: 'cache_key desc',
  })

  const orderItemsPayload = useOrderItems({
    // @ts-expect-error TS2322
    filters: {
      agency_id: isAgent || isEbuAdmin ? undefined : selectedAgencyId,
      ...filters,
    },
  })

  const {
    filteredOrderItems,
    hasFilteredOrderItems,
  } = orderItemsPayload

  const {
    updatedEntities: {
      orderItems: orderItemsUpdatedAt,
      statuses: statusesUpdatedAt,
    },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { orders } = useSelector(reduxState => reduxState.entities)

  const groupedOrderItems = useMemo(() => {
    const groupedItems = groupOrderItemsByDueDate(filteredOrderItems)

    if (!selectedPriorityUserId) return groupedItems

    return sortByPriorityOrderItems({
      groupedItems, orders, selectedPriorityUserId, priorityOnly: !!priorityCampaignUserId,
    })
  }, [JSON.stringify(filters), orderItemsUpdatedAt, statusesUpdatedAt])

  return {
    groupedOrderItems,
    hasFilteredOrderItems,
    loading,
  }
}

export default useOrdersOverview
